/* eslint-disable eqeqeq */
/**
 * Phase 6 Vendor-Port W4 — PLATE HEAT EXCHANGER selector static data (§33.4).
 *
 * Every constant here is VERBATIM from
 * /tmp/ksdm-phase2/django/static/mvc/component_selector/components/
 * plate_heat_exchanger/view/plate_heat_exchanger.selector.js (3,275). Kept out
 * of the component so the selector reads cleanly. Mirrors energyWheelSelectorData
 * (the W3 pattern); PHX needs the dependency graph MORE than EW (§33.4).
 *
 * The two manufacturers are 167 = Recuperator and 52 = Heatex. (The legacy
 * `// Innergy Tech` comment at line 100 is STALE — MFG_MAPPING confirms
 * 167→recuperator, 52→heatex. Do NOT trust that comment.)
 */

import type { DependencyGraphSpec } from './useDependencyGraph';

const MASTER = '/api/v1.0/master/v1.0.0/ahu/plate_heat_exchangers';
const CMS = '/api/v1.0/cms/1/v1.0.0/ahu/plate_heat_exchangers';

/** the phx CMS params doc (legacy fn['current.plate_heat_exchanger.cms']). */
export const PHX_CMS_DOC_URL = `${CMS}/params/`;
/** rating notes (legacy NotesCollection baseUrl). */
export const PHX_NOTES_URL = `${CMS}/notes/`;
/** mfg settings (legacy getMfgSettings, line 2249-2257). */
export const PHX_MFG_SETTINGS_URL = `${CMS}/mfg/settings/`;
/** module configurations (legacy getModuleConfigurations, 2258-2266). */
export const PHX_MODULE_CONFIGS_URL = `${MASTER}/module_configurations/`;
/** air stream collection (legacy AirStreamCollection). */
export const PHX_AIR_STREAMS_URL = '/api/v1.0/cms/1/v1.0.0/ahu/air/air_streams/';
/** pricing methods — legacy self.pricing_methods (the shared PricingMethods
 * collection, plate_heat_exchanger.selector.js:206/831/876). Same per-component
 * master endpoint the ewheel selector uses. Renders the Pricing Method dropdown;
 * a method other than List(1)/User(2) exposes the price_* List fields. */
/**
 * ★ pricing methods are a GLOBAL ahu collection, not a PHX-scoped one — legacy's
 * PricingMethods collection is `/api/v1.0/master/v1.0.0/ahu/pricing_methods/`
 * (component_selector/components/coil/collection/pricing_methods.js:8, shared by
 * every selector). Building it off MASTER gave
 * `…/ahu/plate_heat_exchangers/pricing_methods/`, which 404s; the query then
 * returned nothing and the dialog fell back to a hardcoded List/User pair.
 * The real list is By Application (1) / Cost by User (2) / Price by User (3).
 */
export const PHX_PRICING_METHODS_URL = '/api/v1.0/master/v1.0.0/ahu/pricing_methods/';

/** casing height (legacy getCasingHeight, 2236-2248). */
export const phxCasingHeightUrl = (
  mfgId: number | string,
  seriesId: number | string,
  sizeId?: number | string | null
): string => {
  let url = `${CMS}/params/casing_height/?mfg_id=${mfgId}&series_id=${seriesId}`;
  if (sizeId) url += `&size_id=${sizeId}`;
  return url;
};

/** sizes (legacy getSize, 2267-2279). */
export const phxSizesUrl = (
  mfgId: number | string,
  seriesId: number | string,
  casingHeight?: number | string | null
): string => {
  let url = `${MASTER}/mfg/${mfgId}/series/${seriesId}/sizes/`;
  if (casingHeight) url += `?casing_height=${casingHeight}`;
  return url;
};

/** the catalogue smart-list baseUrl (legacy PhxMasterCollection, line 2818). */
export const PHX_CATALOG_BASE_URL = `${CMS}/all/`;

/**
 * damper-shaft master lists (legacy `collection.damper_shaft` —
 * component_selector/components/plate_heat_exchanger/collection/damper_shaft.js).
 * Only the OUTPUT pane reads them: the Bypass output tab prints the shaft
 * shape / diameter / length for `configuration_id == 2`, and Select then lifts
 * the chosen ids straight back off those spans' `data-id`
 * (plate_heat_exchanger.selector.js:1909-1911 → :2067-2069).
 */
export const PHX_SHAFT_SHAPE_URL = '/api/v1.0/master/v1.0.0/ahu/damper_shaft_shape/';
export const PHX_SHAFT_LENGTH_URL =
  '/api/v1.0/master/v1.0.0/ahu/damper_shaft_external_length/';
export const PHX_SHAFT_DIAMETER_URL = '/api/v1.0/master/v1.0.0/ahu/damper_shaft_diameter/';

/**
 * §32 PHX MFG-id → manufacturer (copy verbatim — plate_heat_exchanger.selector.js
 * line 2667-2670). Re-exported from ratingEnvelope so the selector imports it
 * from one place, but kept named here for clarity / the spec checklist.
 */
export { PHX_MFG_MAPPING } from './ratingEnvelope';

/** legacy MFG_DEFAULT_SERIES (915-918): {167:916, 52:276}. */
export const PHX_MFG_DEFAULT_SERIES: Record<number, number> = { 167: 916, 52: 276 };

/**
 * plate_heat_exchanger.selector.js:54-98 — per-mfg required fields per season,
 * gating which season blocks are SENT to the vendor (and nulled on apply when a
 * season is blank). The ONLY per-mfg difference is the humidity field:
 * Recuperator (167) uses *_rh_*, Heatex (52) uses *_wb_temp_* — the WB-vs-RH
 * toggle (item 14).
 */
export const SUMMER_REQUIRED_FIELDS_FOR_RATE: Record<number, string[]> = {
  167: [
    'oa_air_stream_summer_id', 'ra_air_stream_summer_id',
    'oa_airflow_actual_summer', 'ra_airflow_actual_summer',
    'oa_db_temp_summer', 'ra_db_temp_summer',
    'oa_rh_summer', 'ra_rh_summer',
  ],
  52: [
    'oa_air_stream_summer_id', 'ra_air_stream_summer_id',
    'oa_airflow_actual_summer', 'ra_airflow_actual_summer',
    'oa_db_temp_summer', 'ra_db_temp_summer',
    'oa_wb_temp_summer', 'ra_wb_temp_summer',
  ],
};

export const WINTER_REQUIRED_FIELDS_FOR_RATE: Record<number, string[]> = {
  167: [
    'oa_air_stream_winter_id', 'ra_air_stream_winter_id',
    'oa_airflow_actual_winter', 'ra_airflow_actual_winter',
    'oa_db_temp_winter', 'ra_db_temp_winter',
    'oa_rh_winter', 'ra_rh_winter',
  ],
  52: [
    'oa_air_stream_winter_id', 'ra_air_stream_winter_id',
    'oa_airflow_actual_winter', 'ra_airflow_actual_winter',
    'oa_db_temp_winter', 'ra_db_temp_winter',
    'oa_wb_temp_winter', 'ra_wb_temp_winter',
  ],
};

/**
 * plate_heat_exchanger.selector.js:99-175 — per-mfg REQUIRED_FIELDS (Sets).
 * mfg 167 and 52 have IDENTICAL 35-field sets (byte-for-byte). Used by the
 * setDefaultsIfNullChecker / __checkParents (skip a null parent when it's not
 * required) + the #rate gate.
 */
const PHX_REQUIRED = new Set<string>([
  'mfg_id', 'series_id', 'size_id', 'certification_id', 'temp_rating_id',
  'seal_id', 'max_allowed_dp_id', 'mounting_id', 'plate_orientation_id',
  'configuration_id', 'bank_wide_id', 'bank_high_id', 'bank_deep_id',
  'modul_qty', 'calc_mode', 'type_desired_efficiency', 'min_desired_efficiency',
  'plate_spacing_code', 'plate_mtl_id', 'plate_pattern', 'plate_finish_id',
  'end_plate_style_id', 'end_plate_mtl_id', 'end_plate_finish_id',
  'corner_profile_style_id', 'corner_profile_mtl_id', 'corner_profile_finish',
  'bypass_type_id', 'bypass_location_id', 'bypass_dmr_config_id',
  'bypass_dmr_drive_location_id', 'bypass_dmr_mtl', 'bypass_dmr_fin',
  'recirc_dmr_config_id', 'recirc_dmr_location_id',
]);
export const PHX_REQUIRED_FIELDS: Record<number, Set<string>> = {
  167: PHX_REQUIRED,
  52: PHX_REQUIRED,
};

/**
 * plate_heat_exchanger.selector.js:215-364 — the new-PHX default_values (subset
 * that seeds a blank state; for a SAVED phx the model/params fields override
 * these). Default mfg = 52 (Heatex), series 276.
 */
export const PHX_DEFAULTS: Record<string, unknown> = {
  type_desired_efficiency_id: 3,
  bypass_side_id: 1,
  bypass_dmr_drive_location_id: 1,
  bypass_dmr_mtl_id: 1,
  bypass_dmr_fin_id: 1,
  seal_id: 8,
  mounting_id: 1,
  temp_rating_id: 1,
  configuration_id: 3,
  module_configuration_id: null,
  face_one_definition_id: 2,
  face_two_definition_id: 1,
  plate_orientation_id: 1,
  plate_mtl_id: 3583,
  plate_finish_id: 2,
  end_plate_mtl_id: 264,
  end_plate_finish_id: 2,
  corner_profile_mtl_id: 3583,
  plate_pattern_id: 1,
  corner_profile_finish_id: 1,
  corner_profile_style_id: 3,
  bypass_type_id: 2,
  dmrs_supplied_by_id: 2,
  bypass_location_id: 1,
  bypass_dmr_config_id: 2,
  recirc_dmr_config_id: 2,
  recirc_dmr_location_id: 1,
  // legacy default_values literally says null (js:248) but never SHOWS null: its
  // Method select has no blank <option> (tmpl:1171-1177), so the browser lands on
  // the first pricing_methods row — id 1, "By Application" — and the DOM harvest
  // (js:1620-1640) sends that 1 on rate/save. Seed 1 so dev3 displays and submits
  // what dev does; ewheel's own default_values (js:433) already says 1 outright.
  pricing_method_id: 1,
  calc_mode_id: 1,
  oa_air_stream_summer_id: 1, sa_air_stream_summer_id: 1,
  ra_air_stream_summer_id: 4, ea_air_stream_summer_id: 4,
  oa_air_stream_winter_id: 1, sa_air_stream_winter_id: 1,
  ra_air_stream_winter_id: 4, ea_air_stream_winter_id: 4,
  bank_wide: 1, bank_high: 1, bank_deep: 1,
  height_from_floor: 0,
  min_desired_efficiency: 50,
  generic_mfg: false,
  module_qty: 1,
  end_plate_style_id: null,
  bypass_dmr_location_id: 1,
  oa_sp: 0,
  // Initial value for the Max Allowed PD input; the mfg-settings fetch
  // (PlateHeatExchangerSelector.tsx, max_allowed_apd) overwrites this with the
  // manufacturer's actual cap once it resolves, for any unsaved PHX.
  max_allowed_dp: 1.5,
};

/**
 * plate_heat_exchanger.selector.js:464-775 — the SELECTOR dependency graph
 * (passed to DependenciesHandler). Compacted to the {dependencies,
 * default_values} shape useDependencyGraph consumes. mfg_id → ~20 params;
 * series_id → the same minus mfg; mtl→finish; style→flange; temp→seal;
 * size→end_plate+widths.
 */
const PHX_MFG_DESCENDANTS = [
  'series_id', 'flow_type_id', 'seal_id', 'plate_pattern_id', 'plate_mtl_id',
  'plate_finish_id', 'plate_spacing_code_id', 'end_plate_style_id',
  'end_plate_mtl_id', 'end_plate_finish_id', 'corner_profile_style_id',
  'corner_profile_finish_id', 'corner_profile_mtl_id', 'certification_id',
  'temp_rating_id', 'mounting_id', 'plate_orientation_id', 'configuration_id',
  'bypass_dmr_mtl_id', 'bypass_dmr_fin_id', 'calc_mode_id', 'shape_id',
];

export const PHX_DEPENDENCY_GRAPH: DependencyGraphSpec = {
  mfg_id: {
    dependencies: PHX_MFG_DESCENDANTS,
    default_values: [52, 167],
  },
  series_id: {
    dependencies: PHX_MFG_DESCENDANTS.filter((d) => d !== 'series_id'),
    default_values: [276, 916],
  },
  plate_orientation_id: { dependencies: [], default_values: [1] },
  flow_type_id: { dependencies: [], default_values: [1] },
  mounting_id: { dependencies: [], default_values: [1] },
  configuration_id: { dependencies: [], default_values: [2] },
  calc_mode_id: { dependencies: [], default_values: [1] },
  plate_pattern_id: { dependencies: [], default_values: [1] },
  plate_finish_id: { dependencies: [], default_values: [2] },
  plate_mtl_id: { dependencies: ['plate_finish_id'], default_values: [3583] },
  end_plate_mtl_id: { dependencies: ['end_plate_finish_id'], default_values: [3583, 264] },
  end_plate_finish_id: { dependencies: [], default_values: [4, 2, 1] },
  end_plate_style_id: { dependencies: [], default_values: [1, 5] },
  corner_profile_mtl_id: { dependencies: ['corner_profile_finish_id'], default_values: [3583] },
  corner_profile_finish_id: { dependencies: [], default_values: [2] },
  corner_profile_style_id: { dependencies: [], default_values: [1] },
  // ★ legacy's `size_id.dependencies` is EMPTY — its `end_plate_style_id` entry
  // is COMMENTED OUT in the source (plate_heat_exchanger.selector.js:684-691)
  // and the port transcribed the commented-out line as a live edge. That edge
  // makes `size_id` a URL parent of `end_plate_style_id`, so `phxParamUrl`
  // returned null whenever Size was unset (the normal case — Size is "--" until
  // the vendor picks one), the End Plate Style list never loaded, the select
  // rendered empty, and the rate could not resolve the saved id to its vendor
  // code. Restored to legacy.
  size_id: { dependencies: [], default_values: [] },
  bypass_dmr_fin_id: { dependencies: [], default_values: [1] },
  bypass_dmr_mtl_id: { dependencies: ['bypass_dmr_fin_id'], default_values: [1] },
  temp_rating_id: { dependencies: ['seal_id'], default_values: [1] },
  shape_id: { dependencies: [], default_values: [1] },
  seal_id: { dependencies: [], default_values: [8, 9, 1] },
};

/**
 * The FORM dependency graph (ahu/.../plate_heat_exchanger/view/form.js:124-239 —
 * the parked-MAJOR engine). Normalized to `_id`-suffixed field names (matching
 * the form's select `name`s + PHX_PARAMETER_NAMES). mfg/series → the dependent
 * master-list params; mtl→finish; temp→seal. The grandchild fields (plate_finish
 * needs plate_mtl in its URL path; seal needs temp_rating; end_plate_finish needs
 * end_plate_mtl; bypass_dmr_fin needs bypass_dmr_mtl) — VERIFIED live: the master
 * endpoints 404 without that parent filter and 200 with it. The form selects
 * render their option lists + selected labels from these dependency-loaded master
 * lists (NOT the CMS params doc).
 *
 * NB phxParamUrl ALWAYS prepends mfg/series to the path (the root filters) and
 * then walks the field's NON-mfg/series parents — so plate_finish_id resolves to
 * master/.../mfg/167/series/916/plate_mtl/3583/plate_finishs/.
 */
const PHX_FORM_MFG_DESCENDANTS = [
  'series_id', 'seal_id', 'plate_pattern_id', 'plate_mtl_id', 'plate_finish_id',
  'end_plate_style_id', 'end_plate_mtl_id', 'end_plate_finish_id',
  'corner_profile_style_id', 'corner_profile_finish_id', 'corner_profile_mtl_id',
  'certification_id', 'mounting_id', 'plate_orientation_id', 'configuration_id',
  'bypass_dmr_mtl_id', 'bypass_dmr_fin_id', 'temp_rating_id',
];

export const PHX_FORM_DEPENDENCY_GRAPH: DependencyGraphSpec = {
  mfg_id: { dependencies: PHX_FORM_MFG_DESCENDANTS, default_values: [] },
  series_id: {
    dependencies: PHX_FORM_MFG_DESCENDANTS.filter((d) => d !== 'series_id'),
    default_values: [],
  },
  plate_mtl_id: { dependencies: ['plate_finish_id'], default_values: [] },
  end_plate_mtl_id: { dependencies: ['end_plate_finish_id'], default_values: [] },
  corner_profile_mtl_id: { dependencies: ['corner_profile_finish_id'], default_values: [] },
  bypass_dmr_mtl_id: { dependencies: ['bypass_dmr_fin_id'], default_values: [] },
  temp_rating_id: { dependencies: ['seal_id'], default_values: [] },
};

/**
 * plate_heat_exchanger.selector.js:1833-1850 — recuperator-only output fields
 * nullified explicitly when undefined on a Heatex rate (keeps Heatex from
 * leaving stale DB values).
 */
export const PHX_FIELDS_TO_NULLIFY: string[] = [
  'wet_temp_effectiveness_summer', 'dry_temp_effectiveness_summer',
  'wet_temp_ratio_summer', 'dry_temp_ratio_summer',
  'frost_starting_temp_summer', 'hrs_capacity_summer',
  'erp_1253_2014_summer', 'efficiency_summer_ashrae',
  'wet_temp_effectiveness_winter', 'dry_temp_effectiveness_winter',
  'wet_temp_ratio_winter', 'dry_temp_ratio_winter',
  'frost_starting_temp_winter', 'hrs_capacity_winter',
  'erp_1253_2014_winter', 'efficiency_winter_ashrae',
  'energy_efficiency_en13053', 'hr_class',
];

/**
 * The master-list dependent selects (field → master param key). The option-list
 * URL is master/v1.0.0/ahu/plate_heat_exchangers/<filters><key>s/ (series stays
 * singular; everything else pluralizes). Fields NOT here resolve from the CMS
 * doc (phxCms PHX_CMS_PATHS) or are plain model fields.
 */
export const PHX_PARAMETER_NAMES: Record<string, string> = {
  seal_id: 'seal',
  plate_pattern_id: 'plate_pattern',
  plate_mtl_id: 'plate_mtl',
  plate_finish_id: 'plate_finish',
  end_plate_style_id: 'end_plate_style',
  end_plate_mtl_id: 'end_plate_mtl',
  end_plate_finish_id: 'end_plate_finish',
  corner_profile_style_id: 'corner_profile_style',
  corner_profile_finish_id: 'corner_profile_finish',
  corner_profile_mtl_id: 'corner_profile_mtl',
  certification_id: 'certification',
  temp_rating_id: 'temp_rating',
  mounting_id: 'mounting',
  plate_orientation_id: 'plate_orientation',
  configuration_id: 'configuration',
  bypass_dmr_mtl_id: 'bypass_dmr_mtl',
  bypass_dmr_fin_id: 'bypass_dmr_fin',
};

/**
 * Build the per-parameter master-list option URL (plate_heat_exchanger.selector
 * .js loadParams 1240-1269 + form.js loadParams 268-308). Walks parents into
 * <name>/<id>/ filter segments, then pluralizes the parameter (series stays
 * singular; else +'s'):
 *   master/v1.0.0/ahu/plate_heat_exchangers/<filters><param>s/
 * `parentValues` maps each parent FIELD (e.g. mfg_id) to its current value.
 * Returns null when a required parent is missing (legacy returns []).
 */
export function phxParamUrl(
  field: string,
  parents: string[],
  parentValues: Record<string, unknown>
): string | null {
  // Order the parents: mfg_id then series_id (the root path filters) then the
  // field-specific parent (the mtl/temp filter — VERIFIED: the master endpoint
  // 404s without it for plate_finish/seal/end_plate_finish/bypass_dmr_fin).
  const ordered = [...parents].sort((a, b) => parentRank(a) - parentRank(b));
  let filters = '';
  for (const p of ordered) {
    const paramName = p.split('_id')[0];
    const v = parentValues[p];
    if (v != null && v !== '' && v !== 'None') {
      filters += `${paramName}/${v}/`;
    } else {
      // a missing parent → no URL (legacy returns [] / undefined).
      return null;
    }
  }
  const key = PHX_PARAMETER_NAMES[field] ?? field.replace(/_id$/, '');
  // series stays singular; everything else pluralizes.
  const seg = field === 'series_id' ? 'series' : `${key}s`;
  return `${MASTER}/${filters}${seg}/`;
}

/** parent ordering rank for the URL path (mfg → series → the rest). */
function parentRank(field: string): number {
  if (field === 'mfg_id') return 0;
  if (field === 'series_id') return 1;
  return 2;
}
